import React, { useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Check } from 'lucide-react';

interface RepaymentMethodInputProps {
  value?: { repayment_method?: string };
  onChange?: (value: { repayment_method: string }) => void;
  disabled?: boolean;
}

/**
 * Atomic component for Repayment Method selection
 * Component ID: LOS_RepaymentMethodInput_433
 * Getter API: LOS_GetRepaymentMethod_434
 * Setter API: LOS_SetRepaymentMethod_435
 */
const RepaymentMethodInput: React.FC<RepaymentMethodInputProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  const currentValue = value?.repayment_method || 'EMI';

  const handleChange = useCallback((method: string) => {
    if (onChange && !disabled) {
      onChange({ repayment_method: method });
    }
  }, [onChange, disabled]);

  const methods = [
    {
      value: 'EMI',
      title: 'Monthly Installment (EMI)',
      description: 'Pay a fixed amount every month',
      recommended: true
    },
    {
      value: 'EDI',
      title: 'Daily Revenue Based (EDI)',
      description: 'Pay a percentage of your daily revenue',
      recommended: false
    }
  ];

  return (
    <div className="space-y-2">
      <Label className="text-base font-semibold">
        Repayment Method <span className="text-red-500">*</span>
      </Label>
      <p className="text-sm text-gray-500 mb-3">
        Choose how you want to repay your loan
      </p>
      
      <div className="grid grid-cols-1 gap-3">
        {methods.map((method) => (
          <Card
            key={method.value}
            className={`
              relative p-4 cursor-pointer transition-all border-2
              ${currentValue === method.value 
                ? 'border-intellend-green bg-intellend-green/5'
                : 'border-gray-200 hover:border-intellend-green/30'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            onClick={() => handleChange(method.value)}
          >
            <div className="flex items-start gap-3">
              {/* Radio Circle */}
              <div className={`
                w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5
                ${currentValue === method.value 
                  ? 'border-intellend-green bg-intellend-green'
                  : 'border-gray-300'
                }
              `}>
                {currentValue === method.value && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-sm">{method.title}</h4>
                  {method.recommended && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {method.description}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <p className="text-xs text-gray-500 italic mt-2">
        * Fixed installment is recommended for predictable monthly budgeting
      </p>
    </div>
  );
};

export default RepaymentMethodInput;
